const OP = { Hello: 0, Identify: 1, Identified: 2, Event: 5, Request: 6, RequestResponse: 7 };

async function sha256Base64(input) {
	const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
	return btoa(String.fromCharCode(...new Uint8Array(digest)));
}

// Minimal obs-websocket v5 client: handshake (with password auth), requests, and OBS's current program scene.
// Emits 'status' ('connecting' | 'connected' | 'disconnected' | 'error') and 'scene' (scene name).
export default class ObsWebSocketClient {
	constructor({ url, password, reconnect = false }) {
		this.url = url;
		this.password = password;
		this.reconnect = reconnect;
		this.status = 'idle';
		this.error = null;
		this.listeners = {};
		this.pending = new Map();
		this.ws = null;
		this.closed = false;
	}

	on(event, fn) {
		(this.listeners[event] ||= []).push(fn);
		return this;
	}

	_emit(event, ...args) {
		for(const fn of this.listeners[event] || []) fn(...args);
	}

	_setStatus(status, error) {
		this.status = status;
		this.error = error || null;
		this._emit('status', status, error);
	}

	connect() {
		this.closed = false;
		this._setStatus('connecting');
		let ws;
		try {
			ws = this.ws = new WebSocket(this.url);
		} catch(e) {
			this._setStatus('error', e.message);
			this._scheduleReconnect();
			return this;
		}
		ws.addEventListener('message', ev => this._onMessage(JSON.parse(ev.data)));
		ws.addEventListener('close', () => {
			for(const { reject } of this.pending.values()) reject(new Error('OBS connection closed'));
			this.pending.clear();
			if(this.status !== 'error') this._setStatus('disconnected');
			this._scheduleReconnect();
		});
		ws.addEventListener('error', () => {
			this._setStatus('error', 'Could not connect to OBS');
			ws.close();
		});
		return this;
	}

	close() {
		this.closed = true;
		clearTimeout(this.reconnectTimer);
		this.ws?.close();
	}

	_scheduleReconnect() {
		if(!this.reconnect || this.closed) return;
		clearTimeout(this.reconnectTimer);
		this.reconnectTimer = setTimeout(() => this.connect(), 2000);
	}

	async _onMessage(msg) {
		if(msg.op === OP.Hello) {
			const identify = { rpcVersion: msg.d.rpcVersion };
			if(msg.d.authentication) {
				const { challenge, salt } = msg.d.authentication;
				const secret = await sha256Base64((this.password || '') + salt);
				identify.authentication = await sha256Base64(secret + challenge);
			}
			this.ws.send(JSON.stringify({ op: OP.Identify, d: identify }));
		} else if(msg.op === OP.Identified) {
			this._setStatus('connected');
			this.getCurrentScene().then(name => name && this._emit('scene', name), () => {});
		} else if(msg.op === OP.RequestResponse) {
			const p = this.pending.get(msg.d.requestId);
			if(!p) return;
			this.pending.delete(msg.d.requestId);
			if(msg.d.requestStatus?.result === false) p.reject(new Error(msg.d.requestStatus.comment || 'OBS request failed'));
			else p.resolve(msg.d.responseData);
		} else if(msg.op === OP.Event) {
			this._onEvent(msg.d);
		}
	}

	async _onEvent({ eventType, eventData }) {
		// The program scene pointer flips when a transition starts, but CurrentProgramSceneChanged only fires once
		// it ends — following the start makes the overlay switch in step with what viewers see.
		if(eventType === 'SceneTransitionStarted') {
			const name = await this.getCurrentScene().catch(() => null);
			if(name) this._emit('scene', name);
		} else if(eventType === 'CurrentProgramSceneChanged') {
			this._emit('scene', eventData.sceneName);
		}
	}

	request(requestType, requestData) {
		return new Promise((resolve, reject) => {
			const requestId = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`;
			this.pending.set(requestId, { resolve, reject });
			this.ws.send(JSON.stringify({ op: OP.Request, d: { requestType, requestId, requestData } }));
		});
	}

	async getCurrentScene() {
		return (await this.request('GetCurrentProgramScene', {}))?.currentProgramSceneName;
	}

	async getSceneNames() {
		return ((await this.request('GetSceneList', {}))?.scenes || []).map(s => s.sceneName).reverse();
	}

	// Resolves once connected, rejects on error/timeout. For one-off lookups from the editor.
	whenConnected(timeoutMs = 5000) {
		return new Promise((resolve, reject) => {
			if(this.status === 'connected') return resolve(this);
			const timer = setTimeout(() => reject(new Error('Timed out connecting to OBS')), timeoutMs);
			this.on('status', (status, error) => {
				if(status === 'connected') { clearTimeout(timer); resolve(this); }
				else if(status === 'error') { clearTimeout(timer); reject(new Error(error)); }
			});
		});
	}
}
